import { CountriesService } from './services/CountriesService';
import { DataPreparationService } from './services/DataPreparationService';
import { FormDefaultValuesService } from './services/FormDefaultValuesService';
import { FormMountService } from './services/FormMountService';
import { SingleBillInfoFormService } from './services/SingleBillInfoFormService';
import { TranslationService } from './services/TranslationService';
import { singleBillInfoFormEventBus } from './SingleBillInfoFormEventBus';
import type { Init, SingleBillInfoFormValues } from './types';
import type { EventBus } from '../../helpers';
import { managerHelpers } from '../../helpers';
import type { MergedApiReturnType, MergedDataReturnType, MergedHandlersReturnType } from '../../types';
import { FormServiceFactory } from '../_shared/Form/FormServiceFactory';
import type { FormChangeHandlerService } from '../_shared/Form/services/FormChangeHandlerService';
import type { UIService } from '../_shared/Form/services/UIService';
import type { ValidationService } from '../_shared/Form/services/ValidationService';
import type { BaseFormEvents } from '../_shared/Form/types';
import type { WidgetLifecycle } from '../WidgetLifecycle';
import { WithApi } from '../WithApi';

type AllServices = [
  ValidationService,
  UIService<SingleBillInfoFormValues>,
  FormChangeHandlerService<SingleBillInfoFormValues>,
  FormDefaultValuesService,
  FormMountService,
  SingleBillInfoFormService,
  CountriesService,
  TranslationService,
  DataPreparationService
];

export type SingleBillInfoFormData = MergedDataReturnType<AllServices>;

export type SingleBillInfoFormHandlers = MergedHandlersReturnType<AllServices>;

type TApi = MergedApiReturnType<AllServices>;

export class SingleBillInfoFormManager extends WithApi<TApi> {
  public singleBillInfoFormInit = ({ pageStore, queries, t }: Init) => {
    const [countriesQuery, singleBillQuery, availableCurrenciesQuery, partnerQuery] = queries;
    const pending =
      countriesQuery.isPending ||
      singleBillQuery.isPending ||
      availableCurrenciesQuery.isPending ||
      partnerQuery.isPending;
    const error = countriesQuery.error || singleBillQuery.error || availableCurrenciesQuery.error || partnerQuery.error;

    if (pending) {
      return {
        status: 'pending',
        data: null,
        handlers: null,
      } as const;
    }

    if (error) {
      return {
        status: 'error',
        data: error,
        handlers: {
          retry: () => {
            managerHelpers.retryQueries(queries);
          },
        },
      } as const;
    }

    const countries = new CountriesService(
      countriesQuery.data,
      t,
      FormServiceFactory.createCountriesService(countriesQuery.data, t)
    );
    const translation = new TranslationService(t, FormServiceFactory.createTranslationService(t));

    const validation = FormServiceFactory.createValidationService(countriesQuery.data, t);
    const uiService = FormServiceFactory.createUIService<SingleBillInfoFormValues>(
      () => pageStore.getState().isDisplayingCard,
      () => pageStore.getState().isDirty,
      (isDirty: boolean) => pageStore.setState({ isDirty }),
      (isDisplayingCard: boolean) => pageStore.setState({ isDisplayingCard }),
      (isLoading: boolean) => pageStore.setState({ isLoading }),
      singleBillInfoFormEventBus as EventBus<BaseFormEvents<SingleBillInfoFormValues>>
    );
    const formChangeHandler = FormServiceFactory.createFormChangeHandlerService<SingleBillInfoFormValues>(
      countriesQuery.data,
      singleBillInfoFormEventBus as EventBus<BaseFormEvents<SingleBillInfoFormValues>>,
      (isDirty: boolean) => pageStore.setState({ isDirty }),
      () => uiService.clearFormErrorsFn?.()
    );

    const singleBillInfoForm = new SingleBillInfoFormService(
      singleBillQuery.data,
      validation,
      availableCurrenciesQuery.data,
      pageStore,
      partnerQuery.data.isPartnerProvisionAccountsToStripe(),
      t
    );
    const formDefaultValuesService = new FormDefaultValuesService(countriesQuery.data, singleBillQuery.data);
    const formMountService = new FormMountService(
      pageStore,
      formDefaultValuesService.getDefaultValues,
      singleBillInfoForm.validate
    );
    const dataPreparationService = new DataPreparationService(
      pageStore,
      singleBillInfoForm.isEditing(),
      countriesQuery.data,
      singleBillQuery.data,
      partnerQuery.data,
      formDefaultValuesService.getDefaultValues,
      uiService
    );

    this.initApi({
      ...formDefaultValuesService.api(),
      ...uiService.api(),
      ...formChangeHandler.api(),
      ...singleBillInfoForm.api(),
      ...validation.api(),
      ...formMountService.api(),
      ...translation.api(),
      ...countries.api(),
      ...dataPreparationService.api(),
    });

    return {
      status: 'loaded',
      data: {
        ...formDefaultValuesService.data(),
        ...uiService.data(),
        ...formChangeHandler.data(),
        ...singleBillInfoForm.data(),
        ...countries.data(),
        ...validation.data(),
        ...translation.data(),
        ...dataPreparationService.data(),
      },
      handlers: {
        ...formDefaultValuesService.handlers(),
        ...uiService.handlers(),
        ...formChangeHandler.handlers(),
        ...singleBillInfoForm.handlers(),
        ...countries.handlers(),
        ...validation.handlers(),
        ...translation.handlers(),
        ...dataPreparationService.handlers(),
      },
      lifecycle: {
        onMount() {
          formMountService.initState();
        },
      } satisfies WidgetLifecycle,
    } as const;
  };
}
